import React, {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import ReactSelect from "react-select";
import useAppRouter from "../../../../hooks/useAppRouter";
import {getMenuMaterialTypeSelectOptions, MenuMaterialType} from "../../../../enum/menuMaterial/MenuMaterialType";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {ApiClientError} from "../../../../apis/errors";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import useLoading from "../../../../hooks/useLoading";
import {useSystemMessage} from "../../../../hooks/useSystemMessage";

export interface MenuMaterialFormValues {
  name: string;
  content: string;
  materialTypeId: number;
  level: number | null;
  webUrl: string | null;
  filename: string | null;
  attachment: string | null; // Base64形式
}

interface MenuMaterialFormProps {
  defaultValues: MenuMaterialFormValues;
  isEdit: boolean,
  apiClientError: ApiClientError | null,
  clearApiClientError: () => Promise<void>;
  onSave: (data: MenuMaterialFormValues) => Promise<void>,
}

export const MenuMaterialForm = (props: MenuMaterialFormProps) => {
  const { defaultValues, isEdit, apiClientError, clearApiClientError, onSave } = props;
  const [ fileAccept, setFileAccept ] = useState<string>('*');
  const { danger } = useSystemMessage();
  const { goto } = useAppRouter();
  const withLoading = useLoading();
  const {
    register,
    setValue,
    getValues,
    clearErrors,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    reValidateMode: 'onSubmit',
  });
  const filename = watch('filename');
  const materialTypeId = watch('materialTypeId');
  const uploadSizeLimit = 1024 * 1024 * 5; // 5MB

  useEffect(() => {
    switch (materialTypeId) {
      case MenuMaterialType.MOVIE:
        setFileAccept('video/*');
        break;
      case MenuMaterialType.PDF:
        setFileAccept('.pdf');
        break;
      case MenuMaterialType.IMAGE:
        setFileAccept('image/*');
        break;
      default:
        setFileAccept('*');
        break;
    }
  }, [materialTypeId]);

  const clearFile = async () => {
    setValue('filename', null);
    setValue('attachment', null);
    await clearErrors('attachment');
    if (document.getElementById('file') !== null) {
      (document.getElementById('file') as HTMLInputElement).value = '';
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target?.files!;
    // ファイル選択が未選択の場合
    if (files.length === 0) {
      return;
    }

    if (files[0].size > uploadSizeLimit) {
      danger('アップロードできるファイルのサイズは5MBまでです。');
      return;
    }

    const fileReader = new FileReader();

    // ファイル読み込み後に実行
    fileReader.addEventListener('load', () => {
      if (typeof fileReader.result === 'string') {
        setValue('filename', files[0].name);
        setValue('attachment', fileReader.result);
      }
    });

    fileReader.readAsDataURL(files[0]);
  }

  const handleSave = async (): Promise<void> => {
    await withLoading(async () => {
      await clearApiClientError();
      await handleSubmit(async (data: MenuMaterialFormValues) => {
        await onSave(data);
      })();
    });
  }

  return (
    <>
      <form className="flex flex-col bg-sub min-h-full px-8">
        <div className="text-xl text-primary font-semibold py-6">メニュー資料{ isEdit ? '編集' : '作成' } </div>
        <div className="mb-4 text-danger text-xs">
          { apiClientError?.message }
          { (Object.keys(errors).length > 0) && '入力エラーがあります' }
        </div>

        { /* 名前 */ }
        <div className="mb-4">
          <label htmlFor="name">
            <div className="text-sm font-semibold text-primary mb-1">
              <span>名前</span>
              <span className="ml-2 required">必須</span>
            </div>
            <input
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...register("name",
                makeRegisterOptions('名前', [
                  { type: FormValidationType.REQUIRED },
                  { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                ])
              )}
              id="name"
              className="text-field"
              type="text"
              maxLength={100}
            />
            <div className="mt-1 text-danger text-xs">
              {errors.name?.message}
            </div>
            <ValidationErrorMessages
              {...{
                fieldErrors: apiClientError?.errors,
                field: 'name',
              }}
            />
          </label>
        </div>

        { /* 説明 */ }
        <div className="mb-4">
          <label htmlFor="content">
            <div className="text-sm font-semibold text-primary mb-1">
              説明
            </div>
            <textarea
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...register('content',
                makeRegisterOptions('説明', [
                  { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                ])
              )}
              className="text-field"
              id="content"
              maxLength={255}
            />
            <div className="mt-1 text-danger text-xs">
              {errors.content?.message}
            </div>
            <ValidationErrorMessages
              {...{
                fieldErrors: apiClientError?.errors,
                field: 'content',
              }}
            />
          </label>
        </div>

        { /* 資料タイプ */ }
        <div className="mb-4">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="materialTypeId">
            <div className="text-xs font-semibold text-primary mb-1">
              <span>資料タイプ</span>
              <span className="ml-2 required">必須</span>
            </div>
            <Controller
              name="materialTypeId"
              control={control}
              render={({field}) =>
                (
                  <ReactSelect
                    inputId="materialTypeId"
                    className="w-40 text-xs"
                    options={getMenuMaterialTypeSelectOptions()}
                    value={getMenuMaterialTypeSelectOptions().find(option => option.value === field.value)}
                    onChange={option => {
                      field.onChange(option?.value);
                      clearFile();
                      setValue('webUrl', '');
                    }}
                  />
                )
              }
              rules={
                makeRegisterOptions('資料タイプ', [
                  { type: FormValidationType.REQUIRED },
                ])
              }
            />
            <div className="mt-1 text-danger text-xs">
              {errors.materialTypeId?.message}
            </div>
            <ValidationErrorMessages
              {...{
                fieldErrors: apiClientError?.errors,
                field: 'materialTypeId',
              }}
            />
          </label>
        </div>

        {(materialTypeId === MenuMaterialType.URL) && (
          /* URL */
          <div className={`mb-4 ${ materialTypeId === MenuMaterialType.URL ? '' : 'invisible' }`}>
            <label htmlFor="webUrl">
              <div className="text-sm font-semibold text-primary mb-1">
                <span>URL</span>
                <span className="ml-2 required">必須</span>
              </div>
              <input
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...register("webUrl",
                  makeRegisterOptions('URL', [
                    {
                      type: FormValidationType.REQUIRED_IF,
                      typeName: 'requiredIfMaterialIsUrl',
                      conditionFunc: () => {
                        return getValues('materialTypeId') === MenuMaterialType.URL;
                      }
                    },
                    {
                      type: FormValidationType.URL,
                      typeName: 'url',
                    },
                  ])
                )}
                id="webUrl"
                className="text-field"
                type="text"
                maxLength={500}
              />
              <div className="mt-1 text-danger text-xs">
                {errors.webUrl?.message}
              </div>
              <ValidationErrorMessages
                {...{
                  fieldErrors: apiClientError?.errors,
                  field: 'webUrl',
                }}
              />
            </label>
          </div>
        )}

        {(materialTypeId !== null) && (materialTypeId !== MenuMaterialType.URL) && (
          /* ファイル */
          <div className="mb-4">
            <label htmlFor="file">
              <div className="text-sm font-semibold text-primary mb-1">
                <span>ファイル</span>
                <span className="ml-2 required">必須</span>
              </div>
              <div className="flex items-center pt-2 pl-2">
                <div className="text-sm">{ filename }</div>
                <div className={ filename === null ? '' : 'ml-4' }>
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={() => {
                      document.getElementById('file')?.click();
                    }}
                  >
                    <span>選 択</span>
                    <input
                      id="file"
                      className="hidden"
                      type="file"
                      onChange={handleFileChange}
                      accept={fileAccept}
                    />

                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register(
                        "filename",
                        makeRegisterOptions('ファイル', [
                          {
                            type: FormValidationType.REQUIRED_IF,
                            typeName: 'requiredIfMaterialIsNotUrl',
                            conditionFunc: () => {
                              return getValues('materialTypeId') !== MenuMaterialType.URL;
                            }
                          },
                        ])
                      )}
                      className="hidden"
                      type="text"
                    />
                  </button>
                </div>
              </div>
              <div className="my-2 text-primary text-xs font-semibold">
                ※ アップロードできるファイルのサイズは5MBまでです
              </div>
              <div className="mt-1 text-danger text-xs">
                {errors.filename?.message}
              </div>
              <ValidationErrorMessages
                {...{
                  fieldErrors: apiClientError?.errors,
                  field: 'filename',
                }}
              />
            </label>
          </div>
        )}

        <div className="sticky flex bottom-0 bg-sub mt-auto py-4">
          <button
            className="btn-sub"
            type='button'
            onClick={() => goto('menuMaterialList')}
          >
            キャンセル
          </button>
          <button
            className="btn-primary ml-auto"
            type='button'
            onClick={handleSave}
          >

            保 存
          </button>
        </div>
      </form>
    </>
  );
}